import { Box, Flex, Icon, Tooltip, Text, Typography } from '@fullstory/components';
import { classNames } from '@fullstory/util/fmt';
import React, { ReactNode } from 'react';
import { StarToggle } from '../../StarToggle';
import css from '../ResourceListTable/index.module.scss';
import { ObjectDescription } from '../../ObjectDescription';

export interface ResourceListItemDescriptionProps {
  name: string;
  description: string;
  disabled?: boolean;
  starred: boolean;
  onStar?: () => void; // if not supplied, the star icon will be hidden and starring will not be possible
  icon: React.ComponentPropsWithoutRef<typeof Icon>['name'];
  rightAdornment?: ReactNode;
}

export const ResourceListItemDescription: React.FC<ResourceListItemDescriptionProps> = ({
  name,
  description,
  disabled = false,
  starred,
  onStar,
  icon,
  rightAdornment,
}) => {
  const tooltipTextStyle = ({ ...props }) => (
    <Text appearance="bodySmall" color="default-inverse" className="fs-unmask">
      {props.children}
    </Text>
  );

  return (
    <Flex
      data-component="ResourceListItemDescription"
      className={classNames({ [css.disabled]: disabled })}
      justifyContent="flex-start"
      alignItems="center"
      testId="resource-list-item-description"
    >
      <Flex justifyContent="center">
        {/* padding pushes the icon up if there's a description to be aligned with the first line */}
        <Box paddingRight={12}>
          <Icon name={icon} size={20} color="secondary" />
        </Box>
      </Flex>
      <Box maxWidth={500} overflow="hidden">
        <Flex column alignItems="stretch">
          <Flex justifyContent="flex-start" testId="resource-list-item-name">
            {description ? (
              <Tooltip
                tooltipContent={
                  <Box onClick={event => event.stopPropagation()}>
                    <ObjectDescription textStyle={tooltipTextStyle} description={description} />
                  </Box>
                }
                className={css.tooltipContainer}
              >
                <Typography variant="body3" lineHeight="24px" ellipsize>
                  {name}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body3" lineHeight="24px" ellipsize>
                {name}
              </Typography>
            )}
            {onStar && (
              <Box paddingLeft={16}>
                <StarToggle
                  starred={starred}
                  onClick={evt => {
                    evt.stopPropagation();
                    onStar();
                  }}
                  size={20}
                  disabled={disabled}
                />
              </Box>
            )}
            {rightAdornment}
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};
